import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./map.css";

/* ================= ICONS ================= */

const firmIcon = new L.Icon({
  iconUrl:
    "https://cdn-icons-png.flaticon.com/512/1673/1673221.png",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const userIcon = new L.Icon({
  iconUrl:
    "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

/* ================= OFFICE LOCATION ================= */

/*
  Sarash Jain & Associates
  884/23, Rohtak Rd,
  near Mission Chowk,
  Aggarsain Nagar,
  Sonipat, Haryana 131001
*/

const officePosition = [28.9849752, 77.0158456];

/* ================= MAP AUTO CENTER ================= */

function MapController({ userLocation }) {
  const map = useMap();

  useEffect(() => {
    if (userLocation) {
      map.fitBounds(
        [officePosition, userLocation],
        {
          padding: [60, 60],
          maxZoom: 16,
        }
      );
    } else {
      map.setView(officePosition, 16);
    }
  }, [map, userLocation]);

  return null;
}

/* ================= COMPONENT ================= */

const CustomGoogleMap = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");

  /* ================= GET USER LOCATION ================= */

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationMessage(
        "Location is not supported by this browser."
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([
          position.coords.latitude,
          position.coords.longitude,
        ]);

        setLocationMessage("");
      },
      () => {
        /*
          This is NOT a problem.
          Google Maps can still get the user's
          location when navigation is opened.
        */
        setLocationMessage(
          "We could not access your current location. Google Maps will use your current location when available."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  /* ================= GOOGLE NAVIGATION ================= */

  const openDirections = () => {
    /*
      IMPORTANT:
      Do NOT force browser GPS coordinates here.

      Google Maps will determine the user's current
      location and calculate the route automatically.
    */

    const googleMapsUrl =
      "https://www.google.com/maps/dir/?api=1" +
      "&destination=Sarash%20Jain%20%26%20Associates%2C%20884%2F23%20Rohtak%20Rd%2C%20Aggarsain%20Nagar%2C%20Sonipat%2C%20Haryana%20131001" +
      "&travelmode=driving";

    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  /* ================= VERIFIED GOOGLE LISTING ================= */

  const openGoogleListing = () => {
    const googleListingUrl =
      "https://www.google.com/maps/search/?api=1" +
      "&query=Sarash%20Jain%20%26%20Associates%2C%20884%2F23%20Rohtak%20Rd%2C%20Aggarsain%20Nagar%2C%20Sonipat%2C%20Haryana%20131001";

    window.open(
      googleListingUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /* ================= TILE ================= */

  const tileUrl = darkMode
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  return (
    <div className="map-wrapper">

      {/* ================= MAP ================= */}

      <div className="map-container">

        <MapContainer
          center={officePosition}
          zoom={16}
          scrollWheelZoom={true}
          className="leaflet-map"
        >

          <TileLayer
            url={tileUrl}
            attribution='&copy; OpenStreetMap contributors'
          />

          {/* OFFICE */}

          <Marker
            position={officePosition}
            icon={firmIcon}
          >
            <Tooltip
              permanent
              direction="top"
              offset={[0, -25]}
              className="firm-label"
            >
              <strong>
                Sarash Jain & Associates
              </strong>
            </Tooltip>
          </Marker>

          {/* USER */}

          {userLocation && (
            <Marker
              position={userLocation}
              icon={userIcon}
            >
              <Tooltip
                direction="top"
                offset={[0, -25]}
              >
                Your Location
              </Tooltip>
            </Marker>
          )}

          <MapController
            userLocation={userLocation}
          />

        </MapContainer>

      </div>

      {/* ================= BUTTONS ================= */}

      <div className="map-buttons">

        <button
          type="button"
          className="direction-btn"
          onClick={openDirections}
        >
          🧭 Start Navigation
        </button>

        <button
          type="button"
          className="listing-btn"
          onClick={openGoogleListing}
        >
          📍 View Google Listing
        </button>

        <button
          type="button"
          className="dark-toggle-btn"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? "☀ Light Mode" : "🌙 Dark Mode"}
        </button>

      </div>

      {/* ================= LOCATION MESSAGE ================= */}

      {locationMessage && (
        <p className="location-message">
          {locationMessage}
        </p>
      )}

    </div>
  );
};

export default CustomGoogleMap;